import { CanvasPinIcon } from "./CanvasPinIcon";

/**
 * 地図のピンの色。種類ごとに固定し、物件一覧に入ったピンは種類を問わず LISTED にする。
 * 決定の経緯は Linear の地図UX改善プロジェクトの Decisions（2026-10-14）を参照
 */
export const PIN_COLORS = {
  /** 物件一覧に入っている物件（地図クリックで情報が出た地点を含む） */
  listed: "#2F6FDE",
  /** 不動産検索結果 */
  realEstateSearch: "#8160D0",
  /** 保存した物件リスト */
  savedPlot: "#2E9D57",
  /** 受付帳 */
  receptionBook: "#EC8A2C",
  /** 解析済み登記情報 */
  requestedPicture: "#E0483E",
} as const;

/**
 * 保存した物件リストを複数同時に地図へ出した時の、リストごとの色。
 * 1つ目は PIN_COLORS.savedPlot（緑）で、ほかの種類のピン（青・紫・オレンジ・赤）や
 * 選択中の黄色い外縁と見分けられる色だけを並べる。リストが色の数より多ければ先頭から繰り返す
 */
export const SAVED_PLOT_LIST_COLORS = [
  PIN_COLORS.savedPlot,
  "#0E9AA7",
  "#D63384",
  "#8D6E63",
  "#9E9D24",
  "#37474F",
] as const;

/** 地図に出した順番（0始まり）から、保存した物件リストのピンの色を返す */
export const getSavedPlotListColor = (index: number): string =>
  SAVED_PLOT_LIST_COLORS[index % SAVED_PLOT_LIST_COLORS.length] ??
  PIN_COLORS.savedPlot;

/** 選択中（ポップアップで情報を出している）ピンの外縁の色 */
export const SELECTED_PIN_OUTLINE_COLOR = "#F9AB00";

/** Material の place アイコンの輪郭。幅14・高さ20、頭の中心 (7,7) 半径7、先端 (7,20)。凡例の SVG でも使う */
export const TEARDROP_PATH =
  "M7 0C3.13 0 0 3.13 0 7c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z";
const PATH_WIDTH = 14;
const PATH_HEIGHT = 20;
const PATH_HEAD_CENTER = 7;
const PATH_DOT_RADIUS = 2.6;

interface TeardropStyle {
  /** ピンの幅（CSS px） */
  width: number;
  /** 白い縁の太さ（CSS px） */
  outlineWidth: number;
  /** 白い縁のさらに外側の縁。無ければ付けない */
  ring?: { color: string; width: number };
  shadow: { blur: number; offsetY: number };
  bleed: number;
}

const NORMAL_STYLE: TeardropStyle = {
  width: 24,
  outlineWidth: 2.5,
  shadow: { blur: 4, offsetY: 1.5 },
  bleed: 8,
};

const SELECTED_STYLE: TeardropStyle = {
  width: 30,
  outlineWidth: 3,
  ring: { color: SELECTED_PIN_OUTLINE_COLOR, width: 3 },
  shadow: { blur: 6, offsetY: 2.5 },
  bleed: 14,
};

const SHADOW_COLOR = "rgba(0, 0, 0, 0.32)";

/**
 * ピンの先端を地点からこの分だけ上に浮かせる（CSS px）。
 * 物件の代表点には地番の文字があり、先端をちょうど地点に置くとピンが地番に重なって読めないため。
 * 物件一覧の青ピンは元のピンに重ねて描くので、すべてのしずく型ピンで同じだけ浮かせる
 */
export const TEARDROP_PIN_LIFT_PX = 6;

const createTeardropPinIcon = (
  color: string,
  style: TeardropStyle
): CanvasPinIcon => {
  const scale = style.width / PATH_WIDTH;
  const height = PATH_HEIGHT * scale;
  return new CanvasPinIcon({
    size: [style.width, Math.ceil(height)],
    anchor: [style.width / 2, height + TEARDROP_PIN_LIFT_PX],
    // ポップアップの先端をピンの頭の少し下に合わせる（L.Marker の既定と同じ見え方）
    popupAnchor: [
      0,
      -Math.round(height + TEARDROP_PIN_LIFT_PX - style.width * 0.25),
    ],
    bleed: style.bleed,
    draw: (ctx) => {
      const path = new Path2D(TEARDROP_PATH);
      ctx.scale(scale, scale);
      ctx.lineJoin = "round";
      const outline = style.outlineWidth / scale;
      const outer = style.ring
        ? outline + (style.ring.width * 2) / scale
        : outline;

      // 外側の縁（白、選択中は黄色）を影付きで描き、その上に塗りと白い縁を重ねる
      ctx.save();
      ctx.shadowColor = SHADOW_COLOR;
      ctx.shadowBlur = style.shadow.blur;
      ctx.shadowOffsetY = style.shadow.offsetY;
      ctx.fillStyle = style.ring?.color ?? "#ffffff";
      ctx.strokeStyle = style.ring?.color ?? "#ffffff";
      ctx.lineWidth = outer;
      ctx.fill(path);
      ctx.stroke(path);
      ctx.restore();

      ctx.lineWidth = outline;
      ctx.strokeStyle = "#ffffff";
      ctx.stroke(path);
      ctx.fillStyle = color;
      ctx.fill(path);
      ctx.stroke(path);

      ctx.beginPath();
      ctx.arc(
        PATH_WIDTH / 2,
        PATH_HEAD_CENTER,
        PATH_DOT_RADIUS,
        0,
        Math.PI * 2
      );
      ctx.fillStyle = "#ffffff";
      ctx.fill();
    },
  });
};

const iconCache = new Map<string, CanvasPinIcon>();

/**
 * 丸みのあるしずく型のピンを返す。同じ色・状態のものは同じインスタンスを返すので、
 * ビットマップは色と状態ごとに1枚だけ作られる。
 * @param color 塗りの色（PIN_COLORS のいずれか）
 * @param selected 選択中なら、一回り大きくして外縁を付ける
 */
export const getTeardropPinIcon = (
  color: string,
  { selected = false }: { selected?: boolean } = {}
): CanvasPinIcon => {
  const key = `${color}:${selected ? "selected" : "normal"}`;
  let icon = iconCache.get(key);
  if (!icon) {
    icon = createTeardropPinIcon(
      color,
      selected ? SELECTED_STYLE : NORMAL_STYLE
    );
    iconCache.set(key, icon);
  }
  return icon;
};

/** 情報が出ないクリック地点の丸の色 */
export const PLAIN_CLICK_PIN_COLOR = "#6B7280";

const PLAIN_CLICK_RADIUS = 7.5;

let plainClickPinIcon: CanvasPinIcon | undefined;

/**
 * 情報が出ないクリック地点に置く、小さな灰色の丸（Google マップの「ただのクリック」と同じ扱い）。
 * 中心が地点
 */
export const getPlainClickPinIcon = (): CanvasPinIcon => {
  if (!plainClickPinIcon) {
    const size = PLAIN_CLICK_RADIUS * 2;
    plainClickPinIcon = new CanvasPinIcon({
      size: [size, size],
      anchor: [PLAIN_CLICK_RADIUS, PLAIN_CLICK_RADIUS],
      popupAnchor: [0, -PLAIN_CLICK_RADIUS],
      bleed: 5,
      draw: (ctx) => {
        const c = PLAIN_CLICK_RADIUS;
        ctx.save();
        ctx.shadowColor = SHADOW_COLOR;
        ctx.shadowBlur = 3;
        ctx.shadowOffsetY = 1;
        ctx.beginPath();
        ctx.arc(c, c, c, 0, Math.PI * 2);
        ctx.fillStyle = "#ffffff";
        ctx.fill();
        ctx.restore();
        ctx.beginPath();
        ctx.arc(c, c, c - 2, 0, Math.PI * 2);
        ctx.fillStyle = PLAIN_CLICK_PIN_COLOR;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(c, c, 2, 0, Math.PI * 2);
        ctx.fillStyle = "#ffffff";
        ctx.fill();
      },
    });
  }
  return plainClickPinIcon;
};
